import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./postrenthouse.css";
import API from "../../../../api";
import {
  ArrowLeft,
  X,
  Home,
  MapPin,
  IndianRupee,
  ImagePlus,
  FileText,
  Lightbulb,
  ShieldCheck,
  Upload,
  CheckCircle2,
  Loader2,
  CalendarDays,
  KeyRound,
  Car,
  Droplets,
} from "lucide-react";

export default function PostRentHouse() {
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");

  const [rent, setRent] = useState("");
  const [deposit, setDeposit] = useState("");
  const [availableFrom, setAvailableFrom] = useState("");

  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);

  const [description, setDescription] = useState("");

  const [loading, setLoading] = useState(false);

  /* =================================================
     CLEAN IMAGE PREVIEW
  ================================================= */

  useEffect(() => {
    return () => {
      if (preview) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  /* =================================================
     IMAGE HANDLER
  ================================================= */

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    if (preview) {
      URL.revokeObjectURL(preview);
    }

    setImageFile(file);
    setPreview(URL.createObjectURL(file));
  };

  /* =================================================
     REMOVE IMAGE
  ================================================= */

  const removeImage = () => {
    if (preview) {
      URL.revokeObjectURL(preview);
    }

    setPreview(null);
    setImageFile(null);
  };

  /* =================================================
     SUBMIT
  ================================================= */

  const handleSubmit = async () => {
    if (!localStorage.getItem("token")) {
      alert("Please login again");
      navigate("/login");
      return;
    }

    if (!title || !city || !rent) {
      alert("Title, location and rent are required");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();

      formData.append("title", title);

      formData.append(
        "location[city]",
        city
      );

      formData.append(
        "location[state]",
        state
      );

      formData.append("rent", rent);

      formData.append(
        "deposit",
        deposit || 0
      );

      formData.append(
        "availableFrom",
        availableFrom
      );

      formData.append(
        "description",
        description
      );

      if (imageFile) {
        formData.append(
          "image",
          imageFile
        );
      }

      const res = await API.post(
        "/rent/post",
        formData,
        {
          headers: {
            "Content-Type":
              "multipart/form-data",
          },
        }
      );

      if (res.data.success) {
        alert(
          "Rent house posted successfully"
        );

        navigate("/home");
      } else {
        alert(
          res.data.message ||
            "Failed to post rent house"
        );
      }
    } catch (err) {
      console.error(
        "RENT POST ERROR:",
        err
      );

      alert(
        "Server error. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rent-post-page">

      {/* =================================================
          TOP BAR
      ================================================= */}

      <div className="rent-post-topbar">

        <button
          className="rent-back-btn"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={18} />
          <span>Back</span>
        </button>

        <button
          className="rent-close-btn"
          onClick={() => navigate("/")}
          aria-label="Close"
        >
          <X size={19} />
        </button>

      </div>


      {/* =================================================
          MAIN
      ================================================= */}

      <main className="rent-post-main">

        {/* =================================================
            INTRO
        ================================================= */}

        <section className="rent-post-intro">

          <div className="rent-post-badge">
            <KeyRound size={15} />
            RENTAL LISTING
          </div>

          <h1>
            Rent your house
            <br />
            <span>to the right tenant.</span>
          </h1>

          <p>
            Create a clear rental listing with
            accurate pricing, availability and
            property details.
          </p>


          {/* =================================================
              BENEFITS
          ================================================= */}

          <div className="rent-post-benefits">

            <div className="rent-post-benefit">

              <div className="rent-benefit-icon">
                <IndianRupee size={17} />
              </div>

              <div>
                <strong>
                  Clear Rental Pricing
                </strong>

                <span>
                  Show monthly rent and deposit
                  clearly to potential tenants.
                </span>
              </div>

            </div>


            <div className="rent-post-benefit">

              <div className="rent-benefit-icon">
                <CalendarDays size={17} />
              </div>

              <div>
                <strong>
                  Set Availability
                </strong>

                <span>
                  Let tenants know when the
                  property is available.
                </span>
              </div>

            </div>


            <div className="rent-post-benefit">

              <div className="rent-benefit-icon">
                <Home size={17} />
              </div>

              <div>
                <strong>
                  Describe Your Home
                </strong>

                <span>
                  Mention facilities, furnishing,
                  parking and other details.
                </span>
              </div>

            </div>

          </div>


          {/* =================================================
              TIP
          ================================================= */}

          <div className="rent-post-tip">

            <div className="rent-tip-icon">
              <Lightbulb size={18} />
            </div>

            <div>

              <strong>
                Listing tip
              </strong>

              <p>
                Mention important details such as
                furnishing, parking, water supply,
                nearby transport, preferred tenant
                requirements and other facilities.
              </p>

            </div>

          </div>

        </section>


        {/* =================================================
            FORM CARD
        ================================================= */}

        <section className="rent-post-card">

          {/* =================================================
              HEADER
          ================================================= */}

          <div className="rent-card-header">

            <div className="rent-card-icon">
              <Home size={20} />
            </div>

            <div>

              <span>
                CREATE RENTAL LISTING
              </span>

              <h2>
                Rental Details
              </h2>

              <p>
                Add information about your
                rental property.
              </p>

            </div>

          </div>


          {/* =================================================
              BASIC DETAILS
          ================================================= */}

          <div className="rent-form-section">

            <div className="rent-section-title">

              <FileText size={16} />

              <span>
                Property Information
              </span>

            </div>


            {/* TITLE */}

            <div className="rent-form-group">

              <label>
                House Title
                <span>*</span>
              </label>

              <div className="rent-input-wrapper">

                <Home size={17} />

                <input
                  value={title}
                  onChange={(e) =>
                    setTitle(e.target.value)
                  }
                  placeholder="e.g. 2BHK Apartment"
                />

              </div>

            </div>


            {/* LOCATION */}

            <div className="rent-form-row">

              <div className="rent-form-group">

                <label>
                  City
                  <span>*</span>
                </label>

                <div className="rent-input-wrapper">

                  <MapPin size={17} />

                  <input
                    value={city}
                    onChange={(e) =>
                      setCity(e.target.value)
                    }
                    placeholder="City"
                  />

                </div>

              </div>


              <div className="rent-form-group">

                <label>
                  State
                </label>

                <div className="rent-input-wrapper">

                  <MapPin size={17} />

                  <input
                    value={state}
                    onChange={(e) =>
                      setState(e.target.value)
                    }
                    placeholder="State"
                  />

                </div>

              </div>

            </div>

          </div>


          {/* =================================================
              RENTAL TERMS
          ================================================= */}

          <div className="rent-form-section">

            <div className="rent-section-title">

              <KeyRound size={16} />

              <span>
                Rental Terms
              </span>

            </div>


            {/* RENT + DEPOSIT */}

            <div className="rent-form-row">

              <div className="rent-form-group">

                <label>
                  Monthly Rent
                  <span>*</span>
                </label>

                <div className="rent-price-input">

                  <div className="rent-price-symbol">
                    <IndianRupee size={17} />
                  </div>

                  <input
                    type="number"
                    value={rent}
                    onChange={(e) =>
                      setRent(e.target.value)
                    }
                    placeholder="e.g. 15000"
                  />

                </div>

                {rent && (
                  <span className="rent-value-preview">
                    ₹
                    {Number(
                      rent
                    ).toLocaleString(
                      "en-IN"
                    )}
                    {" / month"}
                  </span>
                )}

              </div>


              <div className="rent-form-group">

                <label>
                  Deposit
                </label>

                <div className="rent-price-input">

                  <div className="rent-price-symbol">
                    <IndianRupee size={17} />
                  </div>

                  <input
                    type="number"
                    value={deposit}
                    onChange={(e) =>
                      setDeposit(
                        e.target.value
                      )
                    }
                    placeholder="e.g. 50000"
                  />

                </div>

                {deposit && (
                  <span className="rent-value-preview">
                    ₹
                    {Number(
                      deposit
                    ).toLocaleString(
                      "en-IN"
                    )}
                  </span>
                )}

              </div>

            </div>


            {/* AVAILABLE DATE */}

            <div className="rent-form-group">

              <label>
                Available From
              </label>

              <div className="rent-input-wrapper">

                <CalendarDays size={17} />

                <input
                  type="date"
                  value={availableFrom}
                  onChange={(e) =>
                    setAvailableFrom(
                      e.target.value
                    )
                  }
                />

              </div>

            </div>

          </div>


          {/* =================================================
              PROPERTY IMAGE
          ================================================= */}

          <div className="rent-form-section">

            <div className="rent-section-title">

              <ImagePlus size={16} />

              <span>
                Property Image
              </span>

            </div>


            {!preview ? (

              <label className="rent-upload-area">

                <input
                  type="file"
                  accept="image/*"
                  onChange={
                    handleImageChange
                  }
                />

                <div className="rent-upload-icon">
                  <Upload size={21} />
                </div>

                <strong>
                  Upload a house image
                </strong>

                <span>
                  Add a clear photo of your
                  rental property
                </span>

                <small>
                  JPG, PNG or other image formats
                </small>

              </label>

            ) : (

              <div className="rent-preview-container">

                <img
                  src={preview}
                  alt="Rental house preview"
                  className="rent-image-preview"
                />

                <div className="rent-preview-overlay">

                  <div>

                    <ImagePlus size={16} />

                    <span>
                      {imageFile?.name ||
                        "Property image"}
                    </span>

                  </div>

                  <button
                    type="button"
                    onClick={removeImage}
                  >
                    <X size={16} />
                    Remove
                  </button>

                </div>

              </div>

            )}

          </div>


          {/* =================================================
              DESCRIPTION
          ================================================= */}

          <div className="rent-form-section">

            <div className="rent-section-title">

              <FileText size={16} />

              <span>
                Property Details
              </span>

            </div>

            <div className="rent-form-group">

              <label>
                Description
              </label>

              <textarea
                value={description}
                onChange={(e) =>
                  setDescription(
                    e.target.value
                  )
                }
                placeholder="Facilities, furnishing, parking, water supply, nearby schools, transport, etc."
              />

              <div className="rent-character-count">
                {description.length} characters
              </div>

            </div>

          </div>


          {/* =================================================
              QUICK INFO
          ================================================= */}

          <div className="rent-quick-info">

            <div>

              <Home size={15} />

              <span>
                Property details
              </span>

            </div>

            <div>

              <Car size={15} />

              <span>
                Mention parking if available
              </span>

            </div>

            <div>

              <Droplets size={15} />

              <span>
                Mention water facilities
              </span>

            </div>

          </div>


          {/* =================================================
              DISCLAIMER
          ================================================= */}

          <div className="rent-disclaimer">

            <ShieldCheck size={18} />

            <p>
              Please provide accurate rental
              information. Civilink is a property
              listing platform and does not guarantee
              the property, owner identity, rental
              terms or legal status. Tenants should
              independently inspect the property and
              verify the owner and agreement before
              making payments.
            </p>

          </div>


          {/* =================================================
              POST
          ================================================= */}

          <button
            className="rent-post-btn"
            onClick={handleSubmit}
            disabled={loading}
          >

            {loading ? (

              <>
                <Loader2
                  size={18}
                  className="rent-loading-icon"
                />

                Posting Rental...
              </>

            ) : (

              <>
                <CheckCircle2 size={18} />

                Post Rent House
              </>

            )}

          </button>


          {/* =================================================
              CANCEL
          ================================================= */}

          <button
            type="button"
            className="rent-cancel-btn"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={16} />

            Cancel Post
          </button>


          <p className="rent-required-note">
            Fields marked with * are required.
          </p>

        </section>

      </main>

    </div>
  );
}